import { useEffect, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-aria-components';
import type { ReportSummary } from '@tallyframe/core';
import { queryKeys, useReports } from '../api/queries';
import { ApiError } from '../api/errors';
import { useSession } from '../app/session-context';
import { Button } from '../ui/Button';
import { CopyButton } from '../ui/CopyButton';
import { PageHeader } from '../ui/PageHeader';
import { Stack } from '../ui/Stack';
import { StatusState } from '../ui/StatusState';
import { Table, type TableColumn } from '../ui/Table';
import { useDocumentTitle } from '../useDocumentTitle';

const LINK =
  'text-ink font-semibold underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink';

type PublishedReport = ReportSummary & { publishedUrl: string };

function columnsFor(workspaceId: string): TableColumn<PublishedReport>[] {
  return [
    {
      id: 'title',
      label: 'Report',
      isRowHeader: true,
      render: (report) => (
        <Link href={`/w/${encodeURIComponent(workspaceId)}/reports/${encodeURIComponent(report.id)}`} className={LINK}>
          {report.title}
        </Link>
      )
    },
    {
      id: 'link',
      label: 'Public link',
      render: (report) => <span className="m-0 p-0 font-mono text-sm break-all">{report.publishedUrl}</span>
    },
    {
      id: 'copy',
      label: 'Copy',
      render: (report) => <CopyButton value={report.publishedUrl} label={`Copy link for ${report.title}`} />
    }
  ];
}

/** The workspace's published reports, from the existing report list (`publishedUrl`); no endpoint of its own. */
export function PublishedLinksRoute(): ReactElement {
  useDocumentTitle('Published links');
  const workspaceId = useSession().snapshot.workspaceId ?? '';
  const reports = useReports(workspaceId);
  const queryClient = useQueryClient();
  const refetchReports = reports.refetch;
  // Matches HomeRoute: useReports has a 30 s staleTime; refetch so a just-published link shows. Follow-up: refetchOnMount option on useReports.
  useEffect(() => {
    if (queryClient.getQueryData(queryKeys.reports(workspaceId)) !== undefined) void refetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, workspaceId]);

  const published = (reports.data?.reports ?? []).filter((r): r is PublishedReport => Boolean(r.publishedUrl));
  const error = reports.error;

  return (
    <div className="p-6">
      <Stack gap={4}>
        <PageHeader title="Published links" />
        {error ? (
          <StatusState
            kind="error"
            title="Published links could not be loaded"
            message={ApiError.isApiError(error) ? error.message : 'Try again.'}
            action={
              <Button size="sm" onPress={() => void reports.refetch()}>
                Try again
              </Button>
            }
          />
        ) : null}
        {reports.data === undefined && !error ? <StatusState kind="loading" title="Loading published links…" /> : null}
        {reports.data !== undefined && published.length === 0 ? (
          <StatusState
            kind="empty"
            title="No published links yet"
            message="Publish a report from its Share menu and its public link appears here."
          />
        ) : null}
        {published.length > 0 ? (
          <div className="m-0 p-0 border border-solid border-line rounded-panel bg-surface overflow-hidden">
            <Table label="Published links" columns={columnsFor(workspaceId)} rows={published} />
          </div>
        ) : null}
      </Stack>
    </div>
  );
}
